import { useMemo } from 'react';
import { Alert, Button, Skeleton, Stack, Text } from '@billda/design-system';
import { useSiteEventsTimeline } from '../../../hooks/useSiteEventsTimeline';
import { groupTimelineByDate } from '../../../utils/dateGrouping';
import { TimeGroupHeader } from '../atoms/TimeGroupHeader';
import { EventRow } from '../molecules/EventRow';

export interface SiteEventsTimelineProps {
  siteId: number | null;
}

/**
 * Site events timeline organism
 *
 * Features:
 * - Aggregates events from site and all child entities (plots, labour items, allocations, variations)
 * - Grouped by date (Today, Yesterday, This Week, Older)
 * - Infinite scroll with "Load More" button
 * - Loading, error, and empty states
 * - Uses React Query for data fetching
 */
export function SiteEventsTimeline({ siteId }: SiteEventsTimelineProps) {
  const {
    events,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
  } = useSiteEventsTimeline(siteId);

  // Group events by date
  const groupedEvents = useMemo(() => groupTimelineByDate(events), [events]);

  // Error state (show only if no data, otherwise show stale data)
  if (error && events.length === 0) {
    return (
      <Alert severity="error" className="mt-4">
        Failed to load site events: {error.message}
      </Alert>
    );
  }

  // Loading state (only when actually loading, not when disabled)
  if (isLoading && events.length === 0) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={80} />
      </Stack>
    );
  }

  // Empty state (no events after successful load)
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16">
        <Text variant="body2" color="secondary">
          No site events yet
        </Text>
        <Text variant="caption" color="disabled" className="mt-0.5">
          Events will appear here as activity occurs across the site
        </Text>
      </div>
    );
  }

  // Timeline content
  return (
    <Stack spacing={3}>
      {/* Event groups */}
      {groupedEvents.map(group => (
        <div key={group.dateGroup}>
          <TimeGroupHeader dateGroup={group.dateGroup} />
          <div className="overflow-hidden rounded-xl border border-border-default bg-surface-default">
            {group.events.map((event, index) => (
              <div key={`${event.eventId}-${index}`}>
                <EventRow event={event} />
                {index < group.events.length - 1 && (
                  <div className="mx-4 border-b border-border-default" />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Load More button */}
      {hasMore && (
        <Button
          variant="outline"
          onClick={() => loadMore()}
          disabled={isLoadingMore}
          fullWidth
        >
          {isLoadingMore ? 'Loading...' : 'Load More Events'}
        </Button>
      )}

      {/* Summary text */}
      <Text variant="caption" color="disabled" className="text-center">
        Showing {events.length} of {totalCount} events
      </Text>
    </Stack>
  );
}
